import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, UserPlus, UserMinus, Calendar, Users, Loader2, Settings, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { authHeaders, getAuthToken } from "@/lib/firebase";
import ReferralCodeCard from "@/components/ReferralCodeCard";

const getInitials = (name) => name ? name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "?";

const ProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  
  // If no userId param, show current user's profile
  const targetUserId = userId || currentUser?.user_id;
  const isOwnProfile = !userId || (currentUser && currentUser.user_id === userId);
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followLoading, setFollowLoading] = useState(false);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [activeTab, setActiveTab] = useState("about");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState("");
  const [editBio, setEditBio] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (targetUserId) {
      loadProfile();
    } else if (!currentUser) {
      navigate("/login");
    }
  }, [targetUserId, currentUser]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const headers = currentUser ? await authHeaders() : {};
      const response = await fetch(`${API}/users/${targetUserId}/profile`, { headers });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setEditName(data.name || "");
        setEditBio(data.bio || "");
      } else {
        toast.error("User not found");
        navigate("/");
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const loadFollowers = async () => {
    try {
      const headers = currentUser ? await authHeaders() : {};
      const response = await fetch(`${API}/users/${targetUserId}/followers`, { headers });
      if (response.ok) {
        setFollowers(await response.json());
      }
    } catch (error) {
      console.error("Error loading followers:", error);
    }
  };

  const loadFollowing = async () => {
    try {
      const headers = currentUser ? await authHeaders() : {};
      const response = await fetch(`${API}/users/${targetUserId}/following`, { headers });
      if (response.ok) {
        setFollowing(await response.json());
      }
    } catch (error) {
      console.error("Error loading following:", error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "followers" && followers.length === 0) {
      loadFollowers();
    } else if (tab === "following" && following.length === 0) {
      loadFollowing();
    }
  };

  const handleFollow = async () => {
    if (!currentUser) {
      toast.error("Please login to follow users");
      navigate("/login");
      return;
    }

    setFollowLoading(true);
    try {
      const headers = await authHeaders();
      const method = profile.is_following ? "DELETE" : "POST";
      const response = await fetch(`${API}/users/${targetUserId}/follow`, { method, headers });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({
          ...prev,
          is_following: data.is_following,
          followers_count: prev.followers_count + (data.is_following ? 1 : -1)
        }));
        toast.success(data.is_following ? "Following!" : "Unfollowed");
      } else {
        toast.error("Failed to update follow status");
      }
    } catch (error) {
      toast.error("Failed to update follow status");
    } finally {
      setFollowLoading(false);
    }
  };

  const handleFollowFromList = async (targetId, isCurrentlyFollowing) => {
    if (!currentUser) {
      toast.error("Please login to follow users");
      return;
    }

    try {
      const headers = await authHeaders();
      const method = isCurrentlyFollowing ? "DELETE" : "POST";
      const response = await fetch(`${API}/users/${targetId}/follow`, { method, headers });
      
      if (response.ok) {
        const data = await response.json();
        setFollowers(prev => prev.map(u => 
          u.user_id === targetId ? { ...u, is_following: data.is_following } : u
        ));
        setFollowing(prev => prev.map(u => 
          u.user_id === targetId ? { ...u, is_following: data.is_following } : u
        ));
        toast.success(data.is_following ? "Following!" : "Unfollowed");
      }
    } catch (error) {
      toast.error("Failed to update follow status");
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const response = await fetch(`${API}/users/me/profile`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ name: editName, bio: editBio })
      });
      
      if (response.ok) {
        setProfile(prev => ({ ...prev, name: editName, bio: editBio }));
        setShowEditModal(false);
        toast.success("Profile updated!");
      } else {
        toast.error("Failed to update profile");
      }
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">User not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-muted rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-semibold">Profile</h1>
          {isOwnProfile && (
            <button onClick={() => setShowEditModal(true)} className="p-2 -mr-2 hover:bg-muted rounded-lg transition-colors">
              <Settings className="w-5 h-5" />
            </button>
          )}
          {!isOwnProfile && <div className="w-9" />}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Profile Header */}
        <div className="text-center mb-6">
          <Avatar className="h-24 w-24 mx-auto mb-4 ring-4 ring-primary/20">
            {profile.picture && <AvatarImage src={profile.picture} />}
            <AvatarFallback className="text-3xl bg-primary/20 text-primary">{getInitials(profile.name)}</AvatarFallback>
          </Avatar>
          <h2 className="text-2xl font-bold">{profile.name}</h2>
          {profile.bio && (
            <p className="text-muted-foreground mt-2 max-w-md mx-auto">{profile.bio}</p>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center justify-center gap-8 mb-6">
          <button 
            onClick={() => handleTabChange("followers")}
            className="text-center hover:opacity-80 transition-opacity"
          >
            <p className="text-2xl font-bold">{profile.followers_count}</p>
            <p className="text-sm text-muted-foreground">Followers</p>
          </button>
          <button 
            onClick={() => handleTabChange("following")}
            className="text-center hover:opacity-80 transition-opacity"
          >
            <p className="text-2xl font-bold">{profile.following_count}</p>
            <p className="text-sm text-muted-foreground">Following</p>
          </button>
          <div className="text-center">
            <p className="text-2xl font-bold">{profile.games_attended}</p>
            <p className="text-sm text-muted-foreground">Games</p>
          </div>
        </div>

        {/* Follow Button */}
        {!isOwnProfile && (
          <div className="flex justify-center mb-6">
            <Button
              size="lg"
              variant={profile.is_following ? "outline" : "default"}
              onClick={handleFollow}
              disabled={followLoading}
              className="min-w-32"
            >
              {followLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : profile.is_following ? (
                <><UserMinus className="w-4 h-4 mr-2" />Unfollow</>
              ) : (
                <><UserPlus className="w-4 h-4 mr-2" />Follow</>
              )}
            </Button>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="w-full grid grid-cols-3 mb-4">
            <TabsTrigger value="about">About</TabsTrigger>
            <TabsTrigger value="followers">Followers</TabsTrigger>
            <TabsTrigger value="following">Following</TabsTrigger>
          </TabsList>

          <TabsContent value="about" className="space-y-4">
            {isOwnProfile && <ReferralCodeCard />}
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-primary" />
                  <span>Hosted {profile.games_hosted} games</span>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-primary" />
                  <span>Attended {profile.games_attended} games</span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="followers">
            {followers.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No followers yet</p>
            ) : (
              <Card>
                <CardContent className="p-0 divide-y divide-border">
                  {followers.map((f) => (
                    <UserListItem 
                      key={f.user_id} 
                      user={f} 
                      currentUserId={currentUser?.user_id}
                      onFollow={() => handleFollowFromList(f.user_id, f.is_following)}
                      onViewProfile={() => navigate(`/profile/${f.user_id}`)}
                    />
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="following">
            {following.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Not following anyone yet</p>
            ) : (
              <Card>
                <CardContent className="p-0 divide-y divide-border">
                  {following.map((f) => (
                    <UserListItem 
                      key={f.user_id} 
                      user={f} 
                      currentUserId={currentUser?.user_id}
                      onFollow={() => handleFollowFromList(f.user_id, f.is_following)}
                      onViewProfile={() => navigate(`/profile/${f.user_id}`)}
                    />
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit Profile Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Your name"
              />
            </div>
            <div>
              <Label htmlFor="edit-bio">Bio</Label>
              <Textarea
                id="edit-bio"
                value={editBio}
                onChange={(e) => setEditBio(e.target.value)}
                placeholder="Tell us about yourself..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleSaveProfile} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// User list item component
const UserListItem = ({ user, currentUserId, onFollow, onViewProfile }) => {
  const isOwnProfile = currentUserId === user.user_id;
  
  return (
    <div className="flex items-center gap-3 p-4">
      <button onClick={onViewProfile} className="flex items-center gap-3 flex-1 min-w-0">
        <Avatar className="h-10 w-10">
          {user.picture && <AvatarImage src={user.picture} />}
          <AvatarFallback className="text-sm bg-primary/20 text-primary">{getInitials(user.name)}</AvatarFallback>
        </Avatar>
        <span className="font-medium truncate">{user.name}</span>
      </button>
      {!isOwnProfile && currentUserId && (
        <Button
          size="sm"
          variant={user.is_following ? "outline" : "default"}
          onClick={onFollow}
          className="shrink-0"
        >
          {user.is_following ? "Following" : "Follow"}
        </Button>
      )}
    </div>
  );
};

export default ProfilePage;
